import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, EyeOff, KeyRound, ArrowLeft } from 'lucide-react';
import { userLogin, forgotPassword, verifyForgotOTP, resetPassword } from '../../api/auth.api';
import { useAuth } from '../../context/AuthContext';
import '../LoginPage.css';

const FEATURES = [
  'Society Maintenance Billing and Collection',
  'Visitor Management & Gatekeeping',
  'Committee Decision-making Tools',
  'Year End Accounting',
  'Document Management and Notices',
  'Resident Communication Hub',
];

const UserLoginPage = () => {
  const navigate = useNavigate();
  const { login, user, role } = useAuth();

  const [form,     setForm]     = useState({ userId: '', password: '' });
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [errors,   setErrors]   = useState({});

  // step: 'credentials' | 'forgot_userid' | 'forgot_otp' | 'forgot_reset'
  const [step,       setStep]       = useState('credentials');
  const [forgotId,   setForgotId]   = useState('');
  const [otp,        setOtp]        = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPass,    setNewPass]    = useState('');
  const [confirmPass,setConfirmPass]= useState('');
  const [subLoading, setSubLoading] = useState(false);

  if (user && role === 'user') { navigate('/dashboard'); return null; }

  const validate = () => {
    const e = {};
    if (!form.userId.trim())   e.userId   = 'User ID is required';
    if (!form.password.trim()) e.password = 'Password is required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      const res = await userLogin(form);
      login(res.data);
      toast.success(`Welcome, ${res.data.user.name}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    if (!forgotId.trim()) { toast.error('Enter your User ID'); return; }
    setSubLoading(true);
    try {
      const res = await forgotPassword({ userId: forgotId.trim(), userType: 'user' });
      toast.success(res.data.message);
      setStep('forgot_otp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSubLoading(false);
    }
  };

  const handleForgotOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim()) { toast.error('Enter the OTP'); return; }
    setSubLoading(true);
    try {
      const res = await verifyForgotOTP({ userId: forgotId.trim(), userType: 'user', otp: otp.trim() });
      setResetToken(res.data.resetToken);
      setStep('forgot_reset');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setSubLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPass.trim()) { toast.error('Enter a new password'); return; }
    if (newPass !== confirmPass) { toast.error('Passwords do not match'); return; }
    setSubLoading(true);
    try {
      await resetPassword({ resetToken, newPassword: newPass });
      toast.success('Password reset successfully. Please log in.');
      setStep('credentials');
      setForgotId(''); setOtp(''); setResetToken(''); setNewPass(''); setConfirmPass('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setSubLoading(false);
    }
  };

  return (
    <div className="ul-page">
      <div className="ul-left">
        <div className="ul-left-inner">
          <h2 className="ul-left-heading">Society Management<br />and Audit Portal</h2>
          <p className="ul-left-sub">
            A complete digital solution for housing societies and residential communities.
          </p>
          <ul className="ul-features">
            {FEATURES.map(f => (
              <li key={f} className="ul-feature-item">
                <span className="ul-feature-check">✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="ul-right">
        <div className="ul-form-wrap">
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <span className="ul-pill">🏠 Housing &amp; Real Estate</span>
          </div>

          {/* ── Login ── */}
          {step === 'credentials' && (
            <>
              <h1 className="ul-form-title">Housing Portal Login</h1>
              <p className="ul-form-sub">Access your society dashboard</p>
              <form onSubmit={handleSubmit} className="ul-form">
                <div className="ul-field">
                  <label className="ul-label">User ID <span style={{ color: 'var(--tc)' }}>*</span></label>
                  <input type="text" className={`ul-input ${errors.userId ? 'ul-input-error' : ''}`}
                    placeholder="Enter your User ID" value={form.userId}
                    onChange={e => setForm(f => ({ ...f, userId: e.target.value }))}
                    autoComplete="username" required />
                  {errors.userId && <span className="ul-error">{errors.userId}</span>}
                </div>
                <div className="ul-field">
                  <label className="ul-label">Password <span style={{ color: 'var(--tc)' }}>*</span></label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPass ? 'text' : 'password'}
                      className={`ul-input ${errors.password ? 'ul-input-error' : ''}`}
                      placeholder="Enter your password" value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      autoComplete="current-password" required style={{ paddingRight: '2.75rem' }} />
                    <button type="button" onClick={() => setShowPass(p => !p)}
                      style={{ position: 'absolute', right: '0.85rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--lt-gray)', display: 'flex', alignItems: 'center' }}>
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <span className="ul-error">{errors.password}</span>}
                </div>
                <button type="submit" className="ul-submit-btn" disabled={loading}>
                  {loading ? 'Signing in…' : 'Click here to Login →'}
                </button>
                <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                  <button type="button" onClick={() => setStep('forgot_userid')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--tc)', textDecoration: 'underline' }}>
                    Forgot Password?
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── Forgot — Enter User ID ── */}
          {step === 'forgot_userid' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <button type="button" onClick={() => setStep('credentials')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tc)', display: 'flex', alignItems: 'center' }}>
                  <ArrowLeft size={16} />
                </button>
                <h1 className="ul-form-title" style={{ margin: 0 }}>Forgot Password</h1>
              </div>
              <p className="ul-form-sub">Enter your User ID to receive an OTP on your registered email.</p>
              <form onSubmit={handleForgotRequest} className="ul-form">
                <div className="ul-field">
                  <label className="ul-label">User ID *</label>
                  <input type="text" className="ul-input" placeholder="Enter your User ID"
                    value={forgotId} onChange={e => setForgotId(e.target.value)} required />
                </div>
                <button type="submit" className="ul-submit-btn" disabled={subLoading}>
                  {subLoading ? 'Sending OTP…' : 'Send OTP →'}
                </button>
              </form>
            </>
          )}

          {/* ── Forgot — Enter OTP ── */}
          {step === 'forgot_otp' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <button type="button" onClick={() => setStep('forgot_userid')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tc)', display: 'flex', alignItems: 'center' }}>
                  <ArrowLeft size={16} />
                </button>
                <h1 className="ul-form-title" style={{ margin: 0 }}>Enter OTP</h1>
              </div>
              <p className="ul-form-sub">Check your registered email for the 6-digit OTP. Valid for 10 minutes.</p>
              <form onSubmit={handleForgotOTP} className="ul-form">
                <div className="ul-field">
                  <label className="ul-label">One-Time Password *</label>
                  <input type="text" className="ul-input" placeholder="Enter 6-digit OTP"
                    value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoComplete="one-time-code" inputMode="numeric" required />
                </div>
                <button type="submit" className="ul-submit-btn" disabled={subLoading}>
                  {subLoading ? 'Verifying…' : 'Verify OTP →'}
                </button>
                <div style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.85rem' }}>
                  <button type="button" onClick={handleForgotRequest}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', opacity: 0.7 }}>
                    Resend OTP
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── Forgot — Reset Password ── */}
          {step === 'forgot_reset' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <KeyRound size={20} style={{ color: 'var(--tc)' }} />
                <h1 className="ul-form-title" style={{ margin: 0 }}>Set New Password</h1>
              </div>
              <p className="ul-form-sub">Choose a strong password for your account.</p>
              <form onSubmit={handleResetPassword} className="ul-form">
                <div className="ul-field">
                  <label className="ul-label">New Password *</label>
                  <input type="password" className="ul-input" placeholder="Min 6 characters"
                    value={newPass} onChange={e => setNewPass(e.target.value)} required />
                </div>
                <div className="ul-field">
                  <label className="ul-label">Confirm Password *</label>
                  <input type="password" className="ul-input" placeholder="Re-enter new password"
                    value={confirmPass} onChange={e => setConfirmPass(e.target.value)} required />
                </div>
                <button type="submit" className="ul-submit-btn" disabled={subLoading}>
                  {subLoading ? 'Resetting…' : 'Reset Password →'}
                </button>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default UserLoginPage;